/**
 * Mixin that resolves a language slug (3-letter) or ISO 639-1 code (2-letter)
 * to the corresponding flagcdn.com SVG URL.
 *
 * flagcdn.com uses ISO 3166-1 alpha-2 country codes, which mostly overlap
 * with ISO 639-1 language codes but differ for several common languages.
 */

// 3-letter slug → ISO 3166-1 alpha-2 country code
const SLUG_MAP = {
    // Already present
    ita: 'it',
    eng: 'gb',
    deu: 'de',
    fra: 'fr',
    esp: 'es',
    rus: 'ru',
    por: 'pt',
    chi: 'cn',
    jpn: 'jp',
    ara: 'sa',
    heb: 'il',
    dut: 'nl',
    ron: 'ro',
    slo: 'sk',  // Slovak (ISO 639-2 bibliographic)
    slk: 'sk',  // Slovak (ISO 639-2 terminological)
    dan: 'dk',
    nor: 'no',
    nob: 'no',  // Norwegian Bokmål
    swe: 'se',
    sve: 'se',
    kor: 'kr',
    pol: 'pl',
    alb: 'al',
    sqi: 'al',  // Albanian (ISO 639-2 terminological)
    tur: 'tr',
    mne: 'me',
    ukr: 'ua',
    cze: 'cz',
    ces: 'cz',  // Czech (ISO 639-2 terminological)
    gre: 'gr',
    ell: 'gr',  // Greek (ISO 639-2 terminological)
    hin: 'in',
    vie: 'vn',
    tha: 'th',
    // European languages
    hrv: 'hr',  // Croatian
    srb: 'rs',  // Serbian (informal)
    srp: 'rs',  // Serbian (ISO 639-2)
    bos: 'ba',  // Bosnian
    slv: 'si',  // Slovenian
    bul: 'bg',  // Bulgarian
    hun: 'hu',  // Hungarian
    fin: 'fi',  // Finnish
    est: 'ee',  // Estonian
    lav: 'lv',  // Latvian
    lat: 'lv',  // Latvian (informal, avoid confusion with Latin)
    lit: 'lt',  // Lithuanian
    mkd: 'mk',  // Macedonian
    mlt: 'mt',  // Maltese
    isl: 'is',  // Icelandic
    ice: 'is',  // Icelandic (ISO 639-2 bibliographic)
    wel: 'gb',  // Welsh (uses GB flag)
    cym: 'gb',  // Welsh (ISO 639-2 terminological)
    gle: 'ie',  // Irish
    cat: 'ad',  // Catalan (uses Andorra flag as closest)
    ltz: 'lu',  // Luxembourgish
    bel: 'by',  // Belarusian
    // Asian languages
    ind: 'id',  // Indonesian
    may: 'my',  // Malay (ISO 639-2 bibliographic)
    msa: 'my',  // Malay (ISO 639-2 terminological)
    kat: 'ge',  // Georgian
    arm: 'am',  // Armenian (ISO 639-2 bibliographic)
    hye: 'am',  // Armenian (ISO 639-2 terminological)
    aze: 'az',  // Azerbaijani
    kaz: 'kz',  // Kazakh
    uzb: 'uz',  // Uzbek
    mon: 'mn',  // Mongolian
    bur: 'mm',  // Burmese (ISO 639-2 bibliographic)
    mya: 'mm',  // Burmese (ISO 639-2 terminological)
    khm: 'kh',  // Khmer (Cambodian)
    lao: 'la',  // Lao
    sin: 'lk',  // Sinhala
    nep: 'np',  // Nepali
    ben: 'bd',  // Bengali
    urd: 'pk',  // Urdu
    fas: 'ir',  // Persian/Farsi (ISO 639-2 terminological)
    per: 'ir',  // Persian/Farsi (ISO 639-2 bibliographic)
    pus: 'af',  // Pashto
    tam: 'lk',  // Tamil (uses Sri Lanka flag)
    tel: 'in',  // Telugu
    mal: 'in',  // Malayalam
    pan: 'in',  // Punjabi
    guj: 'in',  // Gujarati
    // African languages
    afr: 'za',  // Afrikaans
    zul: 'za',  // Zulu
    xho: 'za',  // Xhosa
    sot: 'ls',  // Sesotho
    swa: 'tz',  // Swahili (uses Tanzania flag)
    hau: 'ng',  // Hausa
    ibo: 'ng',  // Igbo
    yor: 'ng',  // Yoruba
    amh: 'et',  // Amharic (Ethiopia) — note: 'et' ISO 639-1 = Estonian, but 'et' country = Ethiopia
    som: 'so',  // Somali
};

// ISO 639-1 (2-letter) → ISO 3166-1 alpha-2 exceptions (when they differ)
const CODE_MAP = {
    en: 'gb',
    zh: 'cn',
    ja: 'jp',
    ar: 'sa',
    he: 'il',
    da: 'dk',
    ko: 'kr',
    sv: 'se',
    sq: 'al',
    uk: 'ua',
    cs: 'cz',
    el: 'gr',
    hi: 'in',
    vi: 'vn',
    et: 'ee',  // Estonian: DeepL/ISO-639-1 'et' → country 'ee' (Estonia)
    nb: 'no',  // Norwegian Bokmål: DeepL 'nb' → country 'no' (Norway)
    lv: 'lv',  // Latvian: same
    lt: 'lt',  // Lithuanian: same
    sl: 'si',  // Slovenian: 'sl' → country 'si' (Slovenia)
    sr: 'rs',  // Serbian: 'sr' → country 'rs' (Serbia)
    bs: 'ba',  // Bosnian: 'bs' → country 'ba' (Bosnia)
    hr: 'hr',  // Croatian: same
    bg: 'bg',  // Bulgarian: same
    ms: 'my',  // Malay: 'ms' → country 'my' (Malaysia)
    id: 'id',  // Indonesian: same
    ka: 'ge',  // Georgian: 'ka' → country 'ge' (Georgia)
};

// Inject shared CSS once (works in both Vue CLI and Vite)
if (typeof document !== 'undefined' && !document.getElementById('flag-mixin-style')) {
    const style = document.createElement('style');
    style.id = 'flag-mixin-style';
    style.textContent = `
        .flag-icon {
            display: inline-block;
            background-size: cover !important;
            background-position: center !important;
        }
    `;
    document.head.appendChild(style);
}


export default {
    methods: {
        /**
         * Accepts either a 3-letter slug (e.g. "eng", "ita") or a 2-letter
         * ISO 639-1 code (e.g. "en", "it") and returns the flag-icons CSS
         * class string (e.g. "fi fi-it"). Use on a <span> element.
         * Returns an empty string for unknown values.
         */
        flagClass(slugOrCode) {
            if (!slugOrCode) return '';

            // Try slug map first (3-letter codes)
            if (SLUG_MAP[slugOrCode]) {
                return `fi fis fi-${SLUG_MAP[slugOrCode]}`;
            }

            // Try 2-letter code with exception map, then fall back to using it directly
            if (slugOrCode.length === 2) {
                const cc = CODE_MAP[slugOrCode] || slugOrCode;
                return `fi fis fi-${cc}`;
            }

            return '';
        },
    },
};
